/**
 * The three states that stop play — READY, PAUSED and GAME OVER — drawn over
 * whichever stage is underneath. SOLO and BALANCE have different fields but the
 * same interruptions, so the panels live here rather than in either renderer.
 *
 * Horizontal placement is relative to the box the caller scrims; vertical
 * placement is absolute in logical canvas units, because the run summary is
 * sized against the canvas rather than against any one field.
 */

import type { GameState } from '../core/game.ts';
import { Painter } from './painter.ts';
import type { HitId, HitList } from './hits.ts';
import {
  GAME_OVER,
  TYPE,
  faces,
  formatCounter,
  formatDuration,
  formatScore,
  onBlock,
  palette,
} from './theme.ts';

/** The region the overlay dims. Its centre anchors everything horizontally. */
export interface OverlayBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** What game over needs from the shell that `GameState` does not carry. */
export interface OverlayView {
  playerName: string;
  mode: 'SOLO' | 'BALANCE' | 'TWIN';
  rank: number | null;
}

const PAUSE_ACTIONS: readonly (readonly [string, string, HitId])[] = [
  ['RESUME', 'ESC', 'pause.resume'],
  ['RESTART', 'R', 'pause.restart'],
  ['MENU', 'M', 'pause.menu'],
];

const OVER_ACTIONS: readonly (readonly [string, string, HitId])[] = [
  ['RESTART', 'R', 'over.restart'],
  ['SCOREBOARD', 'S', 'over.scoreboard'],
  ['MENU', 'M', 'over.menu'],
];

export function scrim(p: Painter, box: OverlayBox): void {
  p.fillRect(box.x, box.y, box.w, box.h, palette().scrim);
}

export function drawReadyOverlay(p: Painter, hits: HitList, box: OverlayBox): void {
  const c = palette();
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  p.typed('READY', cx, cy - 8, TYPE.overlayTitle, c.text, 'center');
  p.typed('PRESS ANY KEY OR CLICK', cx, cy + 24, TYPE.footer, c.textDim, 'center');
  hits.add('game.start', box.x, box.y, box.w, box.h);
}

export function drawPauseOverlay(
  p: Painter,
  hits: HitList,
  box: OverlayBox,
  state: GameState,
): void {
  const c = palette();
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;

  // Two-bar pause mark, built from the same block.
  const barW = 22;
  const barH = 64;
  const top = cy - 150;
  p.bevelRect(cx - barW - 4, top, barW, barH, faces('#63c9d8'), 5);
  p.bevelRect(cx + 4, top, barW, barH, faces('#63c9d8'), 5);

  p.typed('PAUSED', cx, top + barH + 40, TYPE.overlayTitle, c.text, 'center');
  p.text(
    `${state.score} · ${state.lines} LINES · LEVEL ${formatCounter(state.level)}`,
    cx,
    top + barH + 66,
    { size: 11, weight: 500, tracking: 0.2, color: c.textDim, align: 'center' },
  );

  const btnW = 210;
  const btnH = 42;
  let y = top + barH + 88;
  PAUSE_ACTIONS.forEach(([label, key, id], i) => {
    blockAction(p, hits, cx - btnW / 2, y, btnW, btnH, label, key, id, i === 0);
    y += btnH + 8;
  });
}

/**
 * Game over, with the run summary `GameStats` has always collected and never
 * shown. The three exits are full-width blocks rather than key hints — they
 * were already registered as hit regions.
 */
export function drawGameOverOverlay(
  p: Painter,
  hits: HitList,
  box: OverlayBox,
  state: GameState,
  view: OverlayView,
): void {
  const c = palette();
  const cx = box.x + box.w / 2;

  p.typed(
    state.gameOverReason === 'topple' ? 'TOPPLED' : 'GAME OVER',
    cx,
    GAME_OVER.titleY,
    TYPE.overlayTitle,
    c.text,
    'center',
  );
  p.text(`${view.playerName} · ${view.mode}`, cx, GAME_OVER.subtitleY, {
    size: 11,
    weight: 500,
    tracking: 0.24,
    color: c.textLabel,
    align: 'center',
  });

  // Score on a yellow plaque.
  const score = formatScore(state.score);
  const plaqueW = Math.max(160, p.measureTyped(score, TYPE.bigScore) + 22 * 2 + 6 * 2);
  const plaqueH = GAME_OVER.plaqueH;
  p.bevelRect(cx - plaqueW / 2, GAME_OVER.plaqueY, plaqueW, plaqueH, c.accentFaces, 6);
  p.typed(
    score,
    cx,
    GAME_OVER.plaqueY + plaqueH / 2 + TYPE.bigScore.size * 0.36,
    TYPE.bigScore,
    c.onAccent,
    'center',
  );

  p.text(`${state.lines} LINES · LEVEL ${formatCounter(state.level)}`, cx, GAME_OVER.totalsY, {
    size: 11,
    weight: 500,
    tracking: 0.2,
    color: c.textDim,
    align: 'center',
  });

  // Placement on a green block.
  const placement =
    view.rank === null ? 'NO PLACEMENT' : `RANK ${formatCounter(view.rank)} ON BOARD`;
  const rankColor = view.rank === null ? null : '#92c745';
  const rankType = { size: 12, weight: 800, tracking: 0.18 };
  const rankW = p.measureTyped(placement, rankType) + 16 * 2 + 5 * 2;
  const rankH = GAME_OVER.rankH;
  p.bevelRect(
    cx - rankW / 2,
    GAME_OVER.rankY,
    rankW,
    rankH,
    rankColor === null ? c.panelAlt : faces(rankColor),
    5,
  );
  p.typed(
    placement,
    cx,
    GAME_OVER.rankY + rankH / 2 + rankType.size * 0.36,
    rankType,
    rankColor === null ? c.textFaint : onBlock(rankColor).text,
    'center',
  );

  // --- the run ------------------------------------------------------------
  const grid = GAME_OVER.statsGrid;
  p.label('THIS RUN', cx + grid.dx[0], GAME_OVER.runLabelY);
  runSummary(state).forEach(([label, value], i) => {
    const x = cx + grid.dx[i % 2];
    const y = grid.ys[Math.floor(i / 2)];
    p.bevelRect(x, y, grid.w, grid.h, c.panel, 4);
    p.typed(label, x + 8, y + 14, { size: 8, weight: 700, tracking: 0.18 }, c.textLabel);
    p.typed(
      value,
      x + grid.w - 8,
      y + 28,
      { size: 15, weight: 700, tracking: 0.04 },
      c.text,
      'right',
    );
  });

  // --- exits --------------------------------------------------------------
  const actions = GAME_OVER.actions;
  OVER_ACTIONS.forEach(([label, key, id], i) => {
    blockAction(
      p,
      hits,
      cx + actions.dx,
      actions.ys[i],
      actions.w,
      actions.h,
      label,
      key,
      id,
      i === 0,
    );
  });
}

/** Six cells, row-major: the counters `GameStats` accumulates every run. */
function runSummary(state: GameState): readonly (readonly [string, string])[] {
  const stats = state.stats;
  const seconds = stats.elapsedMs / 1000;
  const rate = seconds > 0 ? (stats.piecesPlaced / seconds).toFixed(1) : '0.0';
  return [
    ['PIECES', stats.piecesPlaced.toString()],
    ['TETRIS', formatCounter(stats.tetrises)],
    ['T-SPIN', formatCounter(stats.tspins)],
    ['MAX COMBO', formatCounter(Math.max(0, stats.maxCombo))],
    ['TIME', formatDuration(stats.elapsedMs)],
    ['PIECES / S', rate],
  ];
}

/** The shared overlay button: a green primary, then quiet `panelAlt` blocks. */
function blockAction(
  p: Painter,
  hits: HitList,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  key: string,
  id: HitId,
  primary: boolean,
): void {
  const c = palette();
  const color = '#92c745';
  const on = primary ? onBlock(color) : { text: c.textDim, dim: c.textFaint };
  p.blockButton(x, y, w, h, primary ? faces(color) : c.panelAlt, 5, on.text, on.dim, {
    label,
    labelType: { size: 12, weight: primary ? 800 : 700, tracking: 0.18 },
    hint: key,
    hintType: { size: 10, weight: 700, tracking: 0.1 },
  });
  hits.add(id, x, y, w, h);
}
